import { AlertTriangle, CheckCircle2, CircleDashed, MinusCircle, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Status, carried three ways at once: a word, a shape, and a colour.
 *
 * The word is the one that has to survive -- a screenshot in a bug report is
 * often greyscale, and roughly one reader in twelve cannot separate the red
 * from the green. The icon gives a second channel at a glance; the colour is
 * the third and never the only one.
 */
export type Tone = "ok" | "warn" | "danger" | "muted";

const TONE_CLASS: Record<Tone, string> = {
  ok: "border-ok/40 bg-ok/10 text-ok",
  warn: "border-warn/40 bg-warn/10 text-warn",
  danger: "border-danger/50 bg-danger/15 text-danger",
  muted: "border-border bg-muted text-muted-foreground",
};

const TONE_ICON = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  danger: XCircle,
  muted: CircleDashed,
} as const;

export function StatusChip({
  tone,
  children,
  icon,
  className,
  title,
}: {
  tone: Tone;
  children: ReactNode;
  icon?: "default" | "pause" | undefined;
  className?: string | undefined;
  /** The long form, for the chips whose word is deliberately one word. */
  title?: string | undefined;
}) {
  const Icon = icon === "pause" ? MinusCircle : TONE_ICON[tone];
  return (
    <span
      data-tone={tone}
      {...(title === undefined ? {} : { title })}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        TONE_CLASS[tone],
        className,
      )}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      {children}
    </span>
  );
}
